import { ISendXapiStatement } from '@vedalib/courseAnalitycs'
import type {
  TestGroupingFeedbackSchemaType,
  TestGroupingFontSchemaType,
  TestGroupingSchemaType,
} from '@vedalib/editor/lib/brand'
import type {
  TestGroupingElementValue,
  TestGroupingGroup,
  TestGroupingItem,
} from '@vedalib/editor/lib/elements'
import type { RichTextValue } from '@vedalib/rich-text'
import { createContext } from 'use-context-selector'

import { NOOP } from 'constants/commonConstans'
import type { ElementFeedbackCss, ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { PREVIEW_MODE } from 'services/Store/Project/constants'
import type { IBlockMode, IBlockResult } from 'services/Store/Project/types'

import type { TestGroupingStateType } from './testGroupingTypes'
import type { ValidationMap } from './types'

interface ITestGroupingContextProps {
  destination: TestGroupingGroup[]
  styles: ElementStyleCss<TestGroupingSchemaType>
  font: ElementFontCss<TestGroupingFontSchemaType>
  feedback?: ElementFeedbackCss<TestGroupingFeedbackSchemaType>
  mode: IBlockMode
  blockResult?: IBlockResult
  shuffledAnswers: TestGroupingItem[]
  initialShuffledAnswers: TestGroupingItem[]
  testResult: { [key: string]: boolean }
  validationMap: ValidationMap
  value: TestGroupingElementValue
  stackIndexes: { [groupValue: string]: number }
  selectedGroupId?: string
  selectedAnswerId?: string
  elementId: string
  isActiveElement: boolean
  activeIndex: string
  isFill: boolean
  state: TestGroupingStateType | undefined
  onLabelSelect: (name: string, e: React.MouseEvent) => void
  onChangeLabel: (label: RichTextValue, path: (string | number)[]) => void
  onAddGroup: (groupValue: string, position: 'before' | 'after') => void
  onAddAnswer: (groupValue: string) => void
  onDeleteAnswer: (groupValue: string, answerValue: string) => void
  onDuplicateAnswer: (groupValue: string, answerValue: string) => void
  onDeleteGroup: (groupValue: string) => void
  onDuplicateGroup: (groupValue: string) => void
  onClickOutside: () => void
  setDestination: React.Dispatch<React.SetStateAction<TestGroupingGroup[]>>
  setShuffledAnswers: React.Dispatch<React.SetStateAction<TestGroupingItem[]>>
  setTestResult: React.Dispatch<
    React.SetStateAction<{
      [key: string]: boolean
    }>
  >
  setValidationMap: React.Dispatch<React.SetStateAction<ValidationMap>>
  setStackIndexes: React.Dispatch<React.SetStateAction<{ [groupValue: string]: number }>>
  setSelectedGroupId: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedAnswerId: React.Dispatch<React.SetStateAction<string | undefined>>
  setState: ((value: TestGroupingStateType) => void) | undefined
  // xAPI analytics
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entityAnalyticsConfig: any
  sendXapiData: (data: ISendXapiStatement) => void
  baseObjectExtensions: {
    elementUrl: string
    blockId: string
    sectionId: string
    elementId: string
    companyId: string
    projectId: string
  }
}

const TestGroupingContext = createContext<ITestGroupingContextProps>({
  destination: [],
  styles: {} as ElementStyleCss<TestGroupingSchemaType>,
  font: {} as ElementFontCss<TestGroupingFontSchemaType>,
  feedback: undefined,
  mode: PREVIEW_MODE,
  blockResult: undefined,
  shuffledAnswers: [],
  initialShuffledAnswers: [],
  testResult: {},
  validationMap: {},
  stackIndexes: {},
  value: {
    items: [],
    showResult: true,
  },
  selectedGroupId: undefined,
  elementId: '',
  isActiveElement: false,
  activeIndex: '',
  isFill: false,
  state: {},
  onLabelSelect: NOOP,
  onChangeLabel: NOOP,
  onAddGroup: NOOP,
  onAddAnswer: NOOP,
  onDeleteAnswer: NOOP,
  onDuplicateAnswer: NOOP,
  onDeleteGroup: NOOP,
  onDuplicateGroup: NOOP,
  onClickOutside: NOOP,
  setDestination: NOOP,
  setShuffledAnswers: NOOP,
  setTestResult: NOOP,
  setValidationMap: NOOP,
  setStackIndexes: NOOP,
  setSelectedGroupId: NOOP,
  setSelectedAnswerId: NOOP,
  setState: NOOP,
  // xAPI analytics
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entityAnalyticsConfig: {} as any,
  sendXapiData: NOOP,
  baseObjectExtensions: {
    elementUrl: '',
    blockId: '',
    sectionId: '',
    elementId: '',
    companyId: '',
    projectId: '',
  },
})

export default TestGroupingContext
